import { AppDialog, FormField, TextareaField } from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  useESGCollaborationStore,
  useESGDisclosureSelectionStore,
  useESGReportWorkflowStore,
} from "@/features/esg/hooks/useESGReportStore";
import { DelegationSchema } from "@/features/esg/schemas/esg.schema";
import { useTran } from "@/hooks";
import { Form, Formik } from "formik";
import { observer } from "mobx-react-lite";
import { useState, useRef } from "react";

const DelegateDisclosuresDialog = observer(() => {
  const { t } = useTran();
  const formikRef = useRef();
  const [selectedCodes, setSelectedCodes] = useState([]);

  const { groupedByCategoryForDelegate } = useESGDisclosureSelectionStore();

  const { showDelegateDisclosuresDialog, setShowDelegateDisclosuresDialog } =
    useESGReportWorkflowStore();

  const { sendDelegations } = useESGCollaborationStore();

  const delegatableCodes = Object.values(groupedByCategoryForDelegate)
    .flat()
    .map((d) => d.code);
  const isSelectedAll =
    delegatableCodes.length > 0 &&
    delegatableCodes.every((code) => selectedCodes.includes(code));
  const selectAll = () => setSelectedCodes(delegatableCodes);
  const unselectAll = () => setSelectedCodes([]);

  const selectByCategory = (category) =>
    setSelectedCodes((prev) => {
      const updated = [
        ...prev,
        ...groupedByCategoryForDelegate[category].map((d) => d.code),
      ];
      return [...new Set(updated)];
    });
  const unselectByCategory = (category) =>
    setSelectedCodes((prev) => {
      const codes =
        groupedByCategoryForDelegate[category].map((d) => d.code) || [];
      return prev.filter((c) => !codes.includes(c));
    });

  const toggleDisclosure = (code) =>
    setSelectedCodes((prev) => {
      const index = prev.indexOf(code);
      if (index > -1) {
        return prev.filter((c) => c !== code);
      }
      return [...prev, code];
    });

  const handleCancel = async () => {
    setShowDelegateDisclosuresDialog(false);
    setSelectedCodes([]);
  };

  const handleSubmit = async (values) => {
    await sendDelegations(selectedCodes, values);
    handleCancel();
  };

  return (
    <AppDialog
      size="xl"
      open={showDelegateDisclosuresDialog}
      onOpenChange={(openState) => {
        if (!openState) setShowDelegateDisclosuresDialog(false);
      }}
      title={t("Delegation Request Data")}
      description={t(
        "This delegation request contains only the chosen disclosure and the report name, and no additional information will be provided.",
      )}
      footer={
        <div className="flex gap-x-2 items-center justify-end">
          <Button
            variant="outline"
            onClick={() => setShowDelegateDisclosuresDialog(false)}
          >
            {t("Cancel")}
          </Button>
          <Button onClick={() => formikRef.current.submitForm()}>
            {t("Confirm")}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-x-2">
          <p className="font-semibold text-sm">{t("Select disclosures")}</p>
          <div className="flex items-center gap-x-2">
            <Badge variant="outline">
              {selectedCodes.length} {t("selected")}
            </Badge>
            <Button size="sm" onClick={isSelectedAll ? unselectAll : selectAll}>
              {t("Select All Topics")}
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {Object.keys(groupedByCategoryForDelegate).map((category) => {
            const disclosures = groupedByCategoryForDelegate[category];
            const isSelectedAllByCategory = disclosures.every((d) =>
              selectedCodes.includes(d.code),
            );

            return (
              <div
                className="rounded-radius border border-border"
                key={category}
              >
                <div className="flex items-center justify-between p-3 border-b border-border">
                  <p className="text-sm font-semibold">{t(category)}</p>
                  <Button
                    variant="ghost"
                    className="text-primary text-xs"
                    onClick={() =>
                      isSelectedAllByCategory
                        ? unselectByCategory(category)
                        : selectByCategory(category)
                    }
                    size="sm"
                  >
                    {t(isSelectedAllByCategory ? "Unselect All" : "Select All")}
                  </Button>
                </div>
                <div className="space-y-1 p-3 max-h-[220px] overflow-y-auto">
                  {disclosures.map((d) => {
                    const isChecked = selectedCodes.includes(d.code);

                    return (
                      <div
                        key={d.code}
                        className="flex items-center gap-x-2 cursor-pointer hover:bg-muted rounded-md p-1"
                        onClick={() => toggleDisclosure(d.code)}
                      >
                        <Checkbox
                          id={d.code}
                          checked={isChecked}
                          onCheckedChange={() => toggleDisclosure(d.code)}
                          onClick={(event) => event.stopPropagation()}
                        />
                        <p className="text-xs font-semibold">{d.code}</p>
                        <p className="text-muted-foreground text-xs truncate">
                          {d.title}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <Formik
          initialValues={{ email: "", message: "" }}
          innerRef={formikRef}
          validationSchema={DelegationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, values, handleChange }) => (
            <Form className="space-y-3">
              <FormField
                name="email"
                label={t("Email")}
                placeholder={t("Email")}
                value={values.email}
                onChange={handleChange}
                error={touched.email && errors.email}
                required
              />
              <TextareaField
                name="message"
                label={t("Message")}
                placeholder={t("Message")}
                value={values.message}
                onChange={handleChange}
                error={touched.message && errors.message}
              />
            </Form>
          )}
        </Formik>
      </div>
    </AppDialog>
  );
});

export default DelegateDisclosuresDialog;
